import { useParams, Navigate } from 'react-router-dom';
import { Box, Text, Heading, useTheme } from '@primer/react';
import { CheckCircleIcon } from '@primer/octicons-react';
import ServiceHero from '../components/ServiceHero';
import ServiceCTA from '../components/ServiceCTA';
import ProcessSteps from '../components/ProcessSteps';
import AIEmbrace from '../components/AIEmbrace';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import { serviceDetails } from '../data/serviceDetails';
import { breadcrumbSchema, serviceSchema, SITE_URL } from '../utils/seo';

function ServiceDetail() {
  const { slug } = useParams();
  const detail = serviceDetails[slug];
  const { colorMode } = useTheme();
  const isDark = colorMode === 'night';

  if (!detail) {
    return <Navigate to="/services" replace />;
  }

  const schema = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/services' },
      { name: detail.title, path: `/services/${detail.slug}` },
    ]),
    serviceSchema({
      name: `${detail.title} Services`,
      description: detail.seoDescription,
      path: `/services/${detail.slug}`,
    }),
  ];

  return (
    <Box sx={{ backgroundColor: isDark ? '#05070a' : '#f0f2f5' }}>
      <SEO
        title={detail.seoTitle}
        description={detail.seoDescription}
        keywords={detail.seoKeywords}
        path={`/services/${detail.slug}`}
        schema={schema}
        image={`${SITE_URL}/og/service-${detail.slug}.jpg`}
      />
      <ServiceHero
        headline={detail.headline}
        subtitle={detail.subtitle}
        videoSrc={detail.heroVideo}
        videoLoop={detail.heroVideoLoop ?? true}
        videoPoster={detail.heroPoster}
      />

      <Box id="services" sx={{ paddingX: ['24px', '48px', '100px'], paddingY: [7, 8, 9] }}>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Reveal>
            <Text
              as="p"
              sx={{
                margin: 0,
                marginBottom: 2,
                fontSize: 1,
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(27, 31, 36, 0.55)',
              }}
            >
              What We Offer
            </Text>
            <Heading
              as="h2"
              sx={{
                margin: 0,
                marginBottom: [5, 6, 7],
                fontFamily: "'Fraunces', serif",
                fontWeight: 300,
                fontSize: ['2rem', '2.5rem', '3rem'],
                color: isDark ? '#f5f1ea' : '#1b1f24',
                maxWidth: '640px',
              }}
            >
              {detail.title} services built for enterprise-grade products.
            </Heading>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
              gap: 4,
            }}
          >
            {detail.services.map((service, index) => (
              <Reveal key={service} delay={index * 0.06}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    paddingY: 3,
                    paddingX: 4,
                    borderRadius: '12px',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(27, 31, 36, 0.1)',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  <CheckCircleIcon size={16} fill="#8ec5ff" />
                  <Text sx={{ fontSize: 1, color: isDark ? '#f5f1ea' : '#1b1f24' }}>{service}</Text>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Box>
      </Box>

      <ProcessSteps steps={detail.process} />

      <AIEmbrace />

      <ServiceCTA eyebrow={detail.ctaEyebrow} headline={detail.ctaHeadline} subtitle={detail.ctaSubtitle} />
    </Box>
  );
}

export default ServiceDetail;
